import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import { secureGet } from "../../config/api";
import { formatDateTime, formatDate } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { secureGetPdf } from "../../config/api";
import DeletePopup from "../../components/DeletePopup";
import GeneratePopup from "../../components/GeneratePopUp";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from "../../components/SnackbarNotification";
import Breadcrumbs from "../../components/Breadcrumbs";
import StatusChain, { STATUS_COLORS } from "../../components/StatusChain";
import DeliveryNoteMapping from "../../components/DeliveryNoteMapping";
import UserContext from "../../Shared/UserContext";

import downloadIcon from "../../icons/printer.svg";
import checkIcon from "../../icons/check.svg";
import { PrimaryButton } from "../../components/PrimaryButton";
import AddIcon from "../../icons/plusB.svg";
import { DetailsHeaderVehicleRow } from "../../components/DetailsHeader";
import { isPeintureDesignation } from "../../utils/paint";

const ORDER_STATUSES = [
  { key: "CREATED", label: "Créé", color: "#3872B0" },
  { key: "PARTIALLY_DELIVERED", label: "Partiellement livré", color: "#FEA26F" },
  { key: "DELIVERED", label: "Livré", color: "#06D6A0" },
];

const OrderDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const { t } = useTranslation();

  const [order, setOrder] = useState(null);
  const [deliveryNotesDetailed, setDeliveryNotesDetailed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { state: { role } } = useContext(UserContext);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    try {
      const response = await secureGet(`/carts/${id}`, showSnackbar, { signal });
      const fetchedOrder = response.data;
      setOrder(fetchedOrder);
      console.log("fetchedOrder", fetchedOrder);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch order:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [id, signal]);

  useEffect(() => {
    fetchOrder();

    return () => {
      abortController.abort();
    };
  }, [id, abortController, fetchOrder]);

  useEffect(() => {
    if (!id) return;
    const mappingController = new AbortController();
    (async () => {
      try {
        const response = await secureGet(`/deliveryNotes/cart/${id}`, showSnackbar, { signal: mappingController.signal });
        setDeliveryNotesDetailed(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Failed to fetch delivery notes mapping:", error);
        }
      }
    })();
    return () => mappingController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading || !order) return <LoadingLogo />;

  const displayOrderStatus = order.status ?? ORDER_STATUSES[0].key;
  const currentOrderStatus = ORDER_STATUSES.find((s) => s.key === displayOrderStatus) || ORDER_STATUSES[0];

  const deliveryNotesList = Array.isArray(order.deliveryNotes)
    ? order.deliveryNotes
    : order.deliveryNote
      ? [order.deliveryNote]
      : [];

  const vehicleForDetailsHeader = order.vehicle
    ? {
        brand: order.vehicle?.brand?.name || order.vehicle?.brandMake || "—",
        model: order.vehicle?.model || "—",
        firstRegistrationDate: formatDate(order.vehicle?.firstRegistrationDate) || "—",
        type: order.vehicle?.type || "—",
        color: order.vehicle?.color || "—",
        plate: order.vehicle?.immatriculationNumber || "—",
        vin: order.vehicle?.vinNumber || "—",
      }
    : null;

  const canLinkClientProject =
    role === "ROLE_SERVICE_CONSULTANT" || role === "ROLE_WORKSHOP_MANAGER";
  const projectDetailsId = order.projectId ?? order.project?.id;

  const renderStatut = () => (
    <StatusChain
      items={[
        { key: "bc", label: "BC", color: STATUS_COLORS.active },
        ...(deliveryNotesList.length > 0
          ? deliveryNotesList.map((dn, index) => ({
              key: dn.id ?? index,
              label: dn.deliveryNoteNumber || "BL",
              color: STATUS_COLORS.active,
              onClick: role === "ROLE_STOREKEEPER" && dn?.id
                ? () => history.push({ pathname: `/delivery-notes/details/${dn.id}` })
                : undefined,
            }))
          : [{ key: "bl", label: "BL", color: STATUS_COLORS.missing }]),
      ]}
    />
  );

  return (
    <div className="h-full flex flex-col">
      <Helmet>
        <title>Drivnest - {t("titles.order_details")}</title>
      </Helmet>
      {/* Header Section */}
      <div className="flex-shrink-0 pb-2">
      <div className="mb-1">
        <Breadcrumbs />
        <h1 className="text-[40px] font-semibold text-[#000000] ">{t(`titles.order_details`)}</h1>
        {/* State and Actions */}
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-[10px]">
            <p className="text-[#000000] font-semibold text-[24px]">
              {order.cartNumber}
            </p>
            <div className="flex space-x-1">{renderStatut()}</div>
            {/* Status is now computed automatically from BL delivery coverage (see
                DeliveryNoteServiceImpl.updateCartStatusFromDeliveryCoverage) - read-only display only. */}
            <span
              className="px-1.5 py-1 rounded-[12px] font-semibold text-[14px] text-white"
              style={{ backgroundColor: currentOrderStatus.color }}
            >
              {currentOrderStatus.label}
            </span>
          </div>

          <div className="flex items-center">
            {/* A "Peinture et ingrédients" line keeps the order open to new BLs: how much paint is
                used is only known along the way */}
            {role === "ROLE_STOREKEEPER" && (order.status !== "DELIVERED"
              || (order.details || []).some((cd) => !cd.product && !cd.notForProcurement && isPeintureDesignation(cd.designation))) && (
              <PrimaryButton
                label={t("primaryButtons.add_deliveryNote") || "Créer un bon de livraison"}
                icon={AddIcon}
                onClick={() =>
                  history.push({
                    pathname: "/delivery-notes/add",
                    state: { fromOrder: order },
                  })
                }
              />
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 text-[24px] text-primary font-semibold flex-wrap">
        {order.client?.code || order.client?.name ? (
          canLinkClientProject && order.client?.id ? (
            <Link to={`/clients/details/${order.client.id}`} className="underline">
              {order.client?.code ? `${order.client?.code} - ` : ""}
              {order.client?.name || ""}
            </Link>
          ) : (
            <span>
              {order.client?.code ? `${order.client?.code} - ` : ""}
              {order.client?.name || ""}
            </span>
          )
        ) : (
          <span className="text-gray-500">Client —</span>
        )}

        <span className="text-primary">-</span>

        {order.projectNumber ? (
          canLinkClientProject && projectDetailsId ? (
            <Link to={`/projects/details/${projectDetailsId}`} className="underline">
              {order.projectNumber}
            </Link>
          ) : (
            <span>{order.projectNumber}</span>
          )
        ) : (
          <span className="text-gray-500">—</span>
        )}

        <span className="text-primary">-</span>

        <span className="text-[#707070] font-semibold text-[24px]">
          {order.createdAt ? formatDateTime(order.createdAt) : "—"}
        </span>
      </div>

        <DetailsHeaderVehicleRow vehicle={vehicleForDetailsHeader} />
      </div>

      {/* Product List */}
      <div className="flex-1 overflow-y-auto mt-1">
      {!order.details || order.details.length === 0 ? (
        <div className="text-center py-12 text-gray-500">{t('empty.products')}</div>
      ) : (
        <DeliveryNoteMapping orderDetails={order.details} deliveryNotes={deliveryNotesDetailed} />
      )}
      </div>

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />

    </div>
  );
};

export default OrderDetails;

